import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Btn } from "../UI";
import { FLabel, FInput, FSelect, FTextarea } from "../../pages/engagement/FormControls";
import { Mail, Phone, Ban, CircleSlash } from "lucide-react";

/** Email the signed prescription lines for one pharmacy. Recipient comes from the Pharmacy Directory. */
export function RxEmailForm({ rx, group, onDone, onCancel }) {
  const [note, setNote] = useState("");
  const [secure, setSecure] = useState(false);
  const [busy, setBusy] = useState(false);
  const ph = group.pharmacy;
  const send = async () => {
    setBusy(true);
    try { const r = await api.post(`/prescriptions/${rx.id}/email`, { note, confirm_secure: secure, pharmacy_id: ph.id }); toast.success(`Emailed ${ph.name}`); onDone(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4" data-testid="rx-email-form">
      <div className="flex items-center gap-2 text-sm font-bold text-[#3E2723]"><Mail size={14} /> Email {ph.name}</div>
      <p className="text-xs text-[#6D4C41]">Includes: {group.medications.filter((_, i) => group.active_line_ids.includes(group.line_ids[i])).join(", ")}</p>
      {ph.email ? <p className="text-xs text-[#6D4C41]">Sends to <strong data-testid="rx-email-to">{ph.email}</strong> from the Pharmacy Directory. Pharmacy replies go to the clinic email in Organization Profile.</p>
        : <p className="text-xs font-semibold text-[#C62828]" data-testid="rx-email-missing">This pharmacy has no email in the Pharmacy Directory. Add one in Settings, Pharmacy Directory, then link it again.</p>}
      <div><FLabel>Note to the pharmacy</FLabel><FTextarea data-testid="rx-email-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></div>
      <label className="flex items-start gap-2 text-xs text-[#5D4037]"><input type="checkbox" data-testid="rx-email-secure" checked={secure} onChange={(e) => setSecure(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#4E342E]" />
        This email contains protected health information. I confirm the clinic has a secure email path or agreement with this pharmacy.</label>
      <div className="flex justify-end gap-2"><Btn variant="ghost" type="button" onClick={onCancel}>Cancel</Btn>
        <Btn type="button" data-testid="rx-email-send" disabled={!ph.email || !secure || busy} onClick={send}><Mail size={14} /> {busy ? "Sending" : "Send email"}</Btn></div>
    </div>
  );
}

/** Void a signed prescription with a reason. Linked chart medications are marked discontinued. */
export function RxVoidForm({ rx, onDone, onCancel }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.post(`/prescriptions/${rx.id}/void`, { reason }); toast.success(`${rx.rx_number} voided`); onDone(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#FFCDD2] bg-[#FFF5F5] p-4" data-testid="rx-void-form">
      <div className="flex items-center gap-2 text-sm font-bold text-[#B71C1C]"><Ban size={14} /> Void {rx.rx_number}</div>
      <p className="text-xs text-[#5D4037]">The prescription stays in the client's history and the audit log, marked Voided. Its medications on the Master Medication List are marked discontinued with this reason. This cannot be undone.</p>
      <div><FLabel>Reason *</FLabel><FTextarea data-testid="rx-void-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example, wrong strength entered" /></div>
      <div className="flex justify-end gap-2"><Btn variant="ghost" type="button" onClick={onCancel}>Cancel</Btn>
        <Btn type="button" data-testid="rx-void-confirm" disabled={reason.trim().length < 5 || busy} onClick={save} className="!bg-[#C62828]"><Ban size={14} /> Void prescription</Btn></div>
    </div>
  );
}

/** Discontinue one medication on a signed prescription. The line locks and staff are asked to cancel refills. */
export function RxDiscontinueForm({ rx, line, onDone, onCancel }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.post(`/prescriptions/${rx.id}/lines/${line.id}/discontinue`, { reason }); toast.success(`${line.medication} discontinued`); onDone(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#FFCDD2] bg-[#FFF5F5] p-4" data-testid="rx-discontinue-form">
      <div className="flex items-center gap-2 text-sm font-bold text-[#B71C1C]"><CircleSlash size={14} /> Discontinue {line.medication} {line.strength}</div>
      <p className="text-xs text-[#5D4037]">The medication is marked Discontinued here and on the Master Medication List. It can no longer be printed, emailed, or duplicated. You will be asked to call the pharmacy to cancel any remaining refills.</p>
      <div><FLabel>Reason *</FLabel><FTextarea data-testid="rx-discontinue-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example, dose changed to 400 mg" /></div>
      <div className="flex justify-end gap-2"><Btn variant="ghost" type="button" onClick={onCancel}>Cancel</Btn>
        <Btn type="button" data-testid="rx-discontinue-confirm" disabled={reason.trim().length < 5 || busy} onClick={save} className="!bg-[#C62828]"><CircleSlash size={14} /> Discontinue</Btn></div>
    </div>
  );
}

/** Log a phone call to one pharmacy on the prescription with its outcome. */
export function RxCallForm({ rx, group, outcomes, initialOutcome = "", onDone, onCancel }) {
  const [form, setForm] = useState({ outcome: initialOutcome, contact_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const ph = group.pharmacy;
  const hasDisc = group.line_ids.length > group.active_line_ids.length;
  const list = outcomes.filter((o) => hasDisc || o !== "Remaining refills cancelled");
  const save = async () => {
    setBusy(true);
    try { const r = await api.post(`/prescriptions/${rx.id}/call`, { ...form, pharmacy_id: ph.id }); toast.success("Call logged"); onDone(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4" data-testid="rx-call-form">
      <div className="flex items-center gap-2 text-sm font-bold text-[#3E2723]"><Phone size={14} /> Log a call to {ph.name}{ph.phone ? ` (${ph.phone})` : ""}</div>
      <div className="grid grid-cols-2 gap-3">
        <div><FLabel>Outcome *</FLabel><FSelect data-testid="rx-call-outcome" value={form.outcome} placeholder="Select outcome" onChange={(e) => setForm({ ...form, outcome: e.target.value })}>{list.map((o) => <option key={o} value={o}>{o}</option>)}</FSelect></div>
        <div><FLabel>Spoke with</FLabel><FInput data-testid="rx-call-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Pharmacist name" /></div>
      </div>
      <div><FLabel>Note{form.outcome === "Other" ? " *" : ""}</FLabel><FTextarea data-testid="rx-call-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="What was discussed" /></div>
      <div className="flex justify-end gap-2"><Btn variant="ghost" type="button" onClick={onCancel}>Cancel</Btn>
        <Btn type="button" data-testid="rx-call-save" disabled={!form.outcome || busy} onClick={save}><Phone size={14} /> Save call</Btn></div>
    </div>
  );
}
